import React from "react";
import AppLayout from "@/Layouts/AppLayout";
import OrganizationLayout from "@/Layouts/OrganizationLayout";
import CodeBlock from "@/Components/Blocks/CodeBlock";

const Container = ({ children, className = "" }) => {
  return (
    <div className={`container mx-auto max-w-[580px] ${className}`}>
      {children}
    </div>
  );
};

export default function DocsWebhooks() {
  return (
    <Container className="flex flex-col gap-4">
      <h2 className="text-xl font-bold">About webhooks</h2>
      <p>
        A webhook is a method of augmenting or altering the behavior of a web
        page or web application with custom callbacks. These callbacks may be
        maintained, modified, and managed by third-party users and developers
        who may not necessarily be affiliated with the originating site or
        application. When an event happens in a source system, it sends an HTTP
        POST request to a pre-defined URL, carrying a payload of data related to
        the event. This allows different systems to communicate and respond to
        changes or updates in real-time.
      </p>
      <h2 className="text-xl font-bold">Implementing webhooks</h2>
      <p>
        To automate the reporting of activities you can set up a webhook that
        sends data whenever a relevant event occurs in your system. Here is a
        step-by-step guide:
      </p>
      <h3 className="text-base font-bold">1. Create webhook URL</h3>
      <p>
        First, you need to create a webhook URL to handle incoming webhook
        requests. You can do this by going to the report page for a specific
        activity. There you will find a button to create a new webhook and copy
        its generated URL.
      </p>
      <h3 className="text-base font-bold">2. Post data to the webhook</h3>
      <p>
        Once you have the webhook URL, you can start sending data to it. Posting
        data can be done using a GET or POST request.
      </p>

      <p>
        For a POST request, you can use the following JSON structure to send
        data:
      </p>
      <CodeBlock type="POST application/json">
        {`{
  "start_date"  => "required|nullable|date|date_format:Y-m-d",
  "end_date"  => "sometimes|nullable|date|date_format:Y-m-d",
  "comment"  => "sometimes|nullable|string",
  "input_$\{id\}"  => "required|string|float",
}`}
      </CodeBlock>
      <p>For a GET request, you can use query parameters to send the data:</p>
      <CodeBlock type="GET">
        {`&start_date=yyyy-mm-dd&end_date=yyyy-mm-dd&input_$\{id\}=value&comment=comment`}
      </CodeBlock>

      <ul className="list-disc">
        <p>Notes on Data Structure</p>

        <li className="ml-4">
          start_date: This field is required, can be nullable, and should follow
          the date format Y-m-d. If left empty the current date will be used.
        </li>
        <li className="ml-4">
          end_date: This field is optional, can be nullable, and should follow
          the date format Y-m-d. If left empty start_date will be used.
        </li>
        <li className="ml-4">
          comment: This field is optional and can be a nullable string, if using
          GET request, the string should be URL encoded.
        </li>
        <li
          className="ml-4"
          dangerouslySetInnerHTML={{
            __html: `input_$\{id\}: This dynamic key is based on the ID of each input. At least one is required and can be a string or float depending on the input type. Replace $\{id\} with the ID of the input.`,
          }}
        />
      </ul>

      <h3 className="text-base font-bold">3. Finding the ID for the input</h3>
      <p>
        Inspecting using devtools in the browser will allow you to find the ID
        on the input field where you would normally report data in Emma.
      </p>

      <h2 className="mt-6 text-xl font-bold">Activity types</h2>
      <p>
        There are several activity types in emma that all will require different
        information. Here's a list of the types and their expected values
      </p>

      <h3 className="text-base font-bold">1. Calculate</h3>
      <p>
        For calculate activities, you need to send a numeric value for its only
        input.
      </p>
      <h3 className={"text-base font-bold"}>2. Select (radio/single answer)</h3>
      <p>
        For select activities, you need to send a null value for for the
        selected input and not include any other inputs. If the input allows for
        a value, you need to send a numeric value instead.
      </p>
      <h3 className={"text-base font-bold"}>
        3. Select (checkbox/multi answer)
      </h3>
      <p>
        For multi select activities, you need to send a null value for each
        selected input. If the input allows for a value, you need to send a
        numeric value instead.
      </p>
      <h3 className={"text-base font-bold"}>4. Free text</h3>
      <p>
        For free text activities, you need to send a string value for its only
        input.
      </p>
      <h3 className={"text-base font-bold"}>5. Distribute</h3>
      <p>
        For distribute activities, you need to send a numeric value for every
        input.
      </p>

      <h2 className="mt-6 text-xl font-bold">Example</h2>
      <p>
        Here is an example of how you can send data to a webhook using a POST
        and GET request.
      </p>

      <CodeBlock type="POST application/json">
        {`https://emma.gidas.se/api/webhook/[TEAM_ID]]/[ACTIVITY_ID]?signature=[HASH]

{
  "start_date": "2024-01-01",
  "end_date": "2024-01-31",
  "comment": "This is a comment",
  "input_1": 10,
}`}
      </CodeBlock>

      <CodeBlock type="GET">
        {`https://emma.gidas.se/api/webhook/[TEAM_ID]]/[ACTIVITY_ID]?signature=[HASH]&start_date=2022-01-01&end_date=2022-01-31&input_1=10&comment=This%20is%20a%20comment`}
      </CodeBlock>

      <p>
        In this example, we are sending data for an activity with a start date
        of 2022-01-01, an end date of 2022-01-31, a comment of "This is a
        comment", and a value of 10 for input 1.
      </p>
    </Container>
  );
}

DocsWebhooks.layout = (page) => {
  const backAction = () => {
    window.history.back();
  };

  return (
    <AppLayout title={`Docs - Using webhooks`}>
      <OrganizationLayout
        backAction={{ label: "Back", callback: backAction }}
        header={<Container>Using webhooks to get automatic reports</Container>}
      >
        {page}
      </OrganizationLayout>
    </AppLayout>
  );
};
